import type { StateCreator } from 'zustand';
import { showFailUploadErrorToast } from 'components/core/UploadProgress/showFailUploadErrorToast';
import { documentClient } from 'shared/domain/document/documentClient';
import { documentationClient } from 'shared/domain/documentation/documentationClient';
import { httpQueueClient } from 'shared/domain/httpQueue/httpQueueClient';
import type { FileServicesUnion } from 'shared/domain/messages/httpQueue/eventMessages';
import { Message, Services } from 'shared/domain/messages/message';
import { createUniqueId } from 'shared/utils/id/id';
import { toUploadProgressUpdate } from './model';
import { Progress, UploadProgressSlice } from './types';

export const createUploadProgressSlice: StateCreator<
  UploadProgressSlice,
  [],
  [],
  UploadProgressSlice
> = (set, get) => ({
  files: [],

  addPendingUploadPlaceholders: (items) => {
    const newEntries = items.map((item) => ({
      uniqueFileId: createUniqueId(),
      status: Progress.PENDING as const,
      type: item.type,
      title: item.title,
      titleTranslationKey: item.titleTranslationKey,
    }));

    set({ files: [...get().files, ...newEntries] });

    return newEntries.map((entry) => entry.uniqueFileId);
  },

  markUploadPlaceholderFailed: (uniqueFileId) => {
    get().updateFileWhere((file) => file.uniqueFileId === uniqueFileId, {
      status: Progress.FAILED,
    });
  },

  subscribeToChannelMessages: () => {
    const followUploads =
      (type: FileServicesUnion, failureLogMessage: string) =>
      (event: Message): void => {
        const update = toUploadProgressUpdate(type, event);
        if (!update) return;
        if (update.changes.status === Progress.FAILED) {
          showFailUploadErrorToast(event, failureLogMessage);
        }
        get().updateFileWhere(update.matches, update.changes);
      };

    const unsubscribes = [
      documentationClient.onEvent(
        followUploads(Services.DOCUMENTATIONS, 'Error while uploading documentation')
      ),
      documentClient.onEvent(followUploads(Services.DOCUMENTS, 'Error while uploading document')),
    ];
    return (): void => unsubscribes.forEach((unsubscribe) => unsubscribe());
  },

  abortPendingUploads: () => {
    get().files.forEach((file) => {
      if (file.status === Progress.PENDING && file.localId) {
        httpQueueClient.abortUpload(file.localId, file.type);
      }
    });
  },

  updateFileWhere: (predicate, updates) => {
    set({
      files: get().files.map((file) =>
        predicate(file) ? { ...file, ...updates } : file
      ),
    });
  },

  removeFile: (uniqueFileId) => {
    set({
      files: get().files.filter((file) => file.uniqueFileId !== uniqueFileId),
    });
  },

  clearFiles: () => set({ files: [] }),
});
